import classNames from 'classnames';
import React, { forwardRef, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import styles from './textarea.module.scss';

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Indicates whether the field should be disabled */
  disabled?: boolean;
  /** A text that is shown below the field and affects the styling */
  error?: string;
  /** A text that is shown below the field as help text */
  help?: string;
  /** The text that is shown above the field as a label */
  label?: string;
  /** Maximum number of characters allowed (activates counter) */
  maxLength?: number;
  /** The value of the field */
  value?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      disabled,
      error,
      help,
      id,
      label,
      value: controlledValue,
      maxLength,
      onChange,
      ...rest
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(controlledValue ?? '');
    const value = controlledValue ?? internalValue;

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = event.target.value;

      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }

      onChange?.(event);
    };

    const handleBeforeInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
      if (!maxLength) return;

      if (value.length >= maxLength) {
        event.preventDefault();
      }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!maxLength) return;

      const pasteData = event.clipboardData.getData('text');
      const selectionStart = event.currentTarget.selectionStart;
      const selectionEnd = event.currentTarget.selectionEnd;

      // If the user has text highlighted, this will replace that range
      const newValue =
        value.slice(0, selectionStart) + pasteData + value.slice(selectionEnd);

      if (newValue.length > maxLength) {
        event.preventDefault();
        const trimmedValue = newValue.slice(0, maxLength);

        if (controlledValue === undefined) {
          setInternalValue(trimmedValue);
        }

        onChange?.({
          ...event,
          target: { ...event.target, value: trimmedValue },
        } as unknown as React.ChangeEvent<HTMLTextAreaElement>);
      }
    };

    return (
      <div
        className={classNames(styles.component, className, {
          [styles.hasError]: error,
          [styles.hasLabel]: label,
          [styles.isDisabled]: disabled,
        })}
      >
        <div className={styles.fieldWrapper}>
          {label && (
            <Typography
              className={styles.label}
              htmlFor={id}
              tag="label"
              tagStyle="bodySmallBold"
            >
              {label}
            </Typography>
          )}

          <textarea
            {...rest}
            ref={ref}
            className={styles.field}
            disabled={disabled}
            id={id}
            onBeforeInput={handleBeforeInput}
            onChange={handleChange}
            onPaste={handlePaste}
            value={value}
          />
        </div>

        <div className={styles.infoWrapper}>
          {(error || help) && (
            <Typography className={styles.help} tagStyle="bodySmall">
              {error || help}
            </Typography>
          )}

          {!!maxLength && (
            <Typography
              className={classNames(styles.counter, {
                [styles.isLimitReached]: value.length > maxLength,
              })}
              tagStyle="bodySmall"
            >
              {value.length}/{maxLength}
            </Typography>
          )}
        </div>
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';

export default Textarea;
